import type { QueryClient } from '@tanstack/react-query';
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { upsertChatMessage } from '../../adminPages/Chat/chatCache';
import { api } from '../../api/api';
import { queryKeys } from '../../api/queryKeys';
import { useAuth } from '../../store/auth';
import { useChatSocketState } from '../../store/useChatSocketState';
import type {
  ConversationMessageEntity,
  ConversationResponse,
  ConversationUnreadCountResponse,
} from '../../types/conversation.types';
import { mockMessageId, MockThread, seedAdminThreads } from './adminMockSeed';
import { mockMessage } from './mockMessages';

/**
 * The Dispatcher tab's backend, faked inside the admin app until the real
 * one is deployed (docs/dispatcher-chat-front.md).
 *
 * It answers that contract's routes for its own conversations only - ids from
 * 900000 up, which no real conversation has - and lets every other request
 * through untouched, adding its bucket to the real unread count on the way
 * back. What the socket would push (a customer writing back, the AI's answer)
 * it writes into the same caches the socket handler does, so the inbox, the
 * thread and the badges move exactly as they will for real.
 *
 * Nothing is stored: every page load starts from the seed.
 */

const MOCK_ID_MIN = 900000;
const CUSTOMER_REPLY_MS = 7000;

let installed = false;

type Handler = () => unknown;

const ok = (config: InternalAxiosRequestConfig, data: unknown) =>
  ({
    data: { error: false, data, message: 'Success' },
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  } as AxiosResponse);

/** By the time an adapter runs, axios has already turned the body to JSON. */
const bodyOf = (config: InternalAxiosRequestConfig) => {
  if (typeof config.data !== 'string') return config.data ?? {};
  try {
    return JSON.parse(config.data);
  } catch {
    return {};
  }
};

const viewer = () => {
  const user = useAuth.getState().user;
  return {
    id: user?.id ?? 0,
    name:
      user?.name ||
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
      'You',
    role: user?.role ?? null,
  };
};

export const installAdminPublicChatMock = (queryClient: QueryClient) => {
  if (installed) return;
  installed = true;

  const threads = new Map<number, MockThread>(
    seedAdminThreads().map((thread) => [thread.row.id, thread])
  );
  let lastRealCounts: ConversationUnreadCountResponse | null = null;

  /* ---------------------------------------------------------------------- */
  /* What the socket would do                                               */
  /* ---------------------------------------------------------------------- */

  const refreshLists = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.inboxAll,
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.conversations.unreadCount,
    });
  };

  const isOpen = (id: number) =>
    Number(useChatSocketState.getState().observedConversationId) === id;

  /** Stores a row and pushes it into the open thread, like `new_message`. */
  const push = (
    thread: MockThread,
    fields: Partial<ConversationMessageEntity>
  ) => {
    const message = mockMessage({
      id: mockMessageId(),
      conversationId: thread.row.id,
      side: 'internal',
      ...fields,
    });
    thread.messages.push(message);
    if (message.audience !== 'internal') {
      thread.row.lastMessageAt = message.createdAt;
      thread.row.lastMessagePreview = message.body;
      thread.row.lastMessageSide = message.side;
      thread.row.lastMessageIsAssistant = message.isAssistant;
    }
    /* The team's unread counts the customer - never the AI's answers. */
    if (message.side === 'user' && !isOpen(thread.row.id)) {
      thread.row.unread += 1;
    }
    upsertChatMessage(queryClient, thread.row.id, message);
    refreshLists();
    return message;
  };

  /**
   * The AI is out of a thread for good once a person has written to the
   * customer there, or it handed the thread over - the contract's one rule.
   */
  const aiStopped = (thread: MockThread) =>
    thread.messages.some(
      (message) =>
        (message.side === 'internal' &&
          !message.isAssistant &&
          message.audience !== 'internal') ||
        message.systemEvent === 'handedToHuman'
    );

  /** The customer on the other end writes back once a person answers. */
  const customerAnswers = (thread: MockThread, toWhat: string) => {
    window.setTimeout(() => {
      push(thread, {
        side: 'user',
        sender: { id: null, name: thread.row.name, role: null },
        body: /refund|credit/i.test(toWhat)
          ? 'Ok, thank you. How long does the refund take?'
          : 'Thank you!',
      });
    }, CUSTOMER_REPLY_MS);
  };

  /* ---------------------------------------------------------------------- */
  /* Routes                                                                 */
  /* ---------------------------------------------------------------------- */

  /** The real counts with the dispatcher bucket added, totals included. */
  const withMockCounts = (
    real: ConversationUnreadCountResponse | null
  ): ConversationUnreadCountResponse => {
    const rows = [...threads.values()]
      .map((thread) => thread.row)
      .filter((row) => !row.archived);
    const dispatcher = {
      threads: rows.filter((row) => row.unread > 0).length,
      messages: rows.reduce((sum, row) => sum + row.unread, 0),
    };
    const base = real ?? { threads: 0, messages: 0 };
    return {
      ...base,
      threads: base.threads + dispatcher.threads,
      messages: base.messages + dispatcher.messages,
      dispatcher,
    };
  };

  const list = (params: Record<string, unknown>) => {
    const query = String(params.query ?? '').toLowerCase();
    /* Keyset paging: everything fits on the first page, so a cursor means
       the list is already over. */
    if (params.cursorId) return [];
    return [...threads.values()]
      .map((thread) => thread.row)
      .filter((row) => row.archived === (String(params.archived) === 'true'))
      .filter(
        (row) =>
          String(params.unansweredOnly) !== 'true' || !!row.unansweredSince
      )
      .filter((row) => String(params.unreadOnly) !== 'true' || row.unread > 0)
      .filter(
        (row) =>
          !query ||
          [
            row.name,
            row.locations,
            row.dispatch?.orderId,
            row.dispatch?.trackingUid,
            row.dispatch?.customerPhone,
            row.dispatch?.brandName,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query))
      )
      .sort((a, b) =>
        (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? '')
      )
      .map((row) => ({ ...row }));
  };

  const detail = (thread: MockThread): ConversationResponse => ({
    id: thread.row.id,
    type: thread.row.type,
    title: null,
    imageUrl: null,
    supportUserId: null,
    participants: [],
    archived: thread.row.archived,
    mutedUntil: thread.row.mutedUntil,
    dispatch: thread.row.dispatch,
  });

  const messages = (thread: MockThread, params: Record<string, unknown>) => {
    const cursorId = Number(params.cursorId) || Infinity;
    const limit = Number(params.limit) || 30;
    return thread.messages
      .filter((message) => message.id < cursorId)
      .slice(-limit)
      .reverse()
      .map((message) => ({ ...message }));
  };

  const send = (thread: MockThread, body: Record<string, unknown>) => {
    const duplicate = thread.messages.find(
      (message) => message.clientMessageId === body.clientMessageId
    );
    if (duplicate) return duplicate;

    const me = viewer();
    const audience = body.audience === 'internal' ? 'internal' : 'everyone';

    const message = push(thread, {
      side: 'internal',
      sender: { id: me.id, name: me.name, role: me.role as never },
      clientMessageId: String(body.clientMessageId),
      body: String(body.body ?? ''),
      audience,
    });

    /* A reply to the customer answers them: the thread stops waiting, and
       the AI stays out from here on. A note changes neither. */
    if (audience === 'everyone') {
      thread.row.unansweredSince = null;
      customerAnswers(thread, message.body ?? '');
    }
    return message;
  };

  const route = (config: InternalAxiosRequestConfig): Handler | null => {
    const url = (config.url ?? '').split('?')[0];
    const method = (config.method ?? 'get').toLowerCase();
    const params = (config.params ?? {}) as Record<string, unknown>;

    if (url === '/v1/conversations' && method === 'get') {
      return /dispatcher/.test(String(params.types ?? ''))
        ? () => list(params)
        : null;
    }

    const match = url.match(/^\/v1\/conversations\/(\d+)(\/.*)?$/);
    const id = match ? Number(match[1]) : NaN;
    const thread = id >= MOCK_ID_MIN ? threads.get(id) : undefined;
    if (!thread) return null;
    const rest = match?.[2] ?? '';

    if (rest === '' && method === 'get') return () => detail(thread);
    if (rest === '/messages' && method === 'get') {
      return () => messages(thread, params);
    }
    if (rest === '/messages' && method === 'post') {
      return () => send(thread, bodyOf(config));
    }
    if (rest === '/read') {
      return () => {
        thread.row.unread = 0;
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.inboxAll,
        });
        return withMockCounts(lastRealCounts);
      };
    }
    if (rest === '/block') {
      return () => ({ ...thread.row });
    }
    if (rest === '/archive') {
      return () => {
        thread.row.archived = !!bodyOf(config).archived;
        return null;
      };
    }
    if (rest === '/mute') {
      return () => {
        thread.row.mutedUntil = bodyOf(config).mutedUntil ?? null;
        return null;
      };
    }
    const translate = rest.match(/^\/messages\/(\d+)\/translate$/);
    if (translate) {
      return () => {
        const target = thread.messages.find(
          (message) => message.id === Number(translate[1])
        );
        return {
          messageId: Number(translate[1]),
          sourceLanguage: 'en',
          targetLanguage: 'es',
          text: `(ES) ${target?.body ?? ''}`,
        };
      };
    }
    const edit = rest.match(/^\/messages\/(\d+)$/);
    if (edit) {
      return () => {
        const target = thread.messages.find(
          (message) => message.id === Number(edit[1])
        );
        if (!target) return null;
        if (method === 'delete') {
          Object.assign(target, {
            body: null,
            deletedAt: new Date().toISOString(),
          });
        } else {
          Object.assign(target, {
            body: String(bodyOf(config).body ?? target.body),
            editedAt: new Date().toISOString(),
          });
        }
        return { ...target };
      };
    }
    /* Uploads have nowhere to go in a mock. */
    return () => {
      throw Object.assign(new Error('Not available in the chat mock.'), {
        response: {
          status: 400,
          data: { message: 'Not available in the chat mock.' },
        },
      });
    };
  };

  api.interceptors.request.use((config) => {
    const handler = route(config);
    if (handler) config.adapter = async () => ok(config, handler());
    return config;
  });

  api.interceptors.response.use((response) => {
    const url = response.config.url ?? '';
    if (
      /^\/v1\/conversations\/(unread-count|read-all|\d+\/read)$/.test(url) &&
      response.data?.data &&
      !(Number(url.split('/')[3]) >= MOCK_ID_MIN)
    ) {
      if (url.endsWith('read-all')) {
        threads.forEach((thread) => {
          thread.row.unread = 0;
        });
      }
      lastRealCounts = response.data.data;
      response.data = {
        ...response.data,
        data: withMockCounts(response.data.data),
      };
    }
    return response;
  });

  /* ---------------------------------------------------------------------- */
  /* Something happening on its own, so the tab is seen to move             */
  /* ---------------------------------------------------------------------- */

  /* The AI picks up the fresh question a little after the page opens: the
     phase while the thread is open, as `assistant_thinking` would, then its
     answer. */
  const fresh = threads.get(900004);
  window.setTimeout(() => {
    if (!fresh || aiStopped(fresh)) return;
    if (isOpen(fresh.row.id)) {
      useChatSocketState.getState().setAssistantState('checkingDelivery');
    }
    window.setTimeout(() => {
      if (aiStopped(fresh)) return;
      if (isOpen(fresh.row.id)) {
        useChatSocketState.getState().setAssistantState(null);
      }
      push(fresh, {
        isAssistant: true,
        sender: { id: null, name: 'vDrive Assistant', role: null },
        body: 'Hi, I am the vDrive assistant, an AI. Your driver is on the way to pick up your order at Burger Barn - the route can look odd while they head to the restaurant. You can follow them live on the map.',
      });
    }, 2500);
  }, 20000);
};
